import { ajax } from './apiUtils';
import { getDataAll } from './dataUtils';
import { insertHtmlWithScripts } from './domUtils';

/**
 * Запрашивает содержимое для выдвижной панели (off-canvas) и отображает его на странице.
 * Предварительно удаляет существующие off-canvas элементы для предотвращения дублирования.
 *
 * @param {string} selector CSS-селектор элемента, инициирующего запрос.
 * @param {string} path URL адрес запроса для получения содержимого off-canvas.
 */
export function showCanvas(selector, url, additionalData = {}) {
  // Выбор элемента по селектору без использования jQuery
  const element = typeof selector === 'string' ? document.querySelector(selector) : selector;
  if (!element) {
    console.error(`Элемент с селектором "${selector}" не найден.`);
    return;
  }

  // Получение данных из элемента (предполагается, что функция getDataAll определена)
  const data = Object.assign({}, getDataAll(element), additionalData);

  // Отправка данных с помощью класса SendData (предполагается, что он определён)
  new SendData({ url: url, data: data })
    .then((response) => {
      // Вставка HTML-контента в <body> и выполнение скриптов
      const insertedElements = insertHtmlWithScripts({ html: response.answer, parent: document.body });

      // Поиск только что добавленного off-canvas элемента
      const offCanvasElement = insertedElements.querySelector('.offcanvas');
      if (!offCanvasElement) {
        console.error('Не найден элемент с классом "offcanvas" в ответе.');
        return;
      }

      // Создание экземпляра Bootstrap Offcanvas для нового элемента
      const newOffCanvas = new bootstrap.Offcanvas(offCanvasElement);

      // Добавление обработчика события после скрытия off-canvas
      offCanvasElement.addEventListener('hidden.bs.offcanvas', function () {
        // Удаление элемента из DOM
        offCanvasElement.remove();
      });

      // Показ нового off-canvas
      newOffCanvas.show();
    })
    .catch((error) => {
      console.error('Ошибка при отправке данных:', error);
    });
}

/**
 * Отправляет асинхронный POST-запрос на указанный URL и обновляет содержимое элемента, выбранного по CSS-селектору,
 * полученным в ответе содержимым. Функция учитывает ситуацию, когда в ответе приходит элемент с тем же идентификатором,
 * что и у целевого элемента, и в таком случае вставляет только его дочернее содержимое.
 *
 * @param {Object} options Конфигурационный объект функции, включающий:
 *  - {HTMLElement|null} elm (необязательно) - элемент, инициирующий запрос. Используется для извлечения данных
 *    из data-атрибутов, если не предоставлены явно через параметр data.
 *  - {string} url - URL-адрес, по которому будет отправлен запрос.
 *  - {string} selector - CSS-селектор элемента, содержимое которого будет обновлено ответом сервера.
 *  - {Object|null} data (необязательно) - объект с дополнительными данными, которые будут отправлены на сервер.
 *    Если не указан, данные могут быть извлечены из элемента elm, если тот предоставлен.
 *
 * Функция выполняет POST-запрос к указанному URL, отправляя данные в формате JSON. При успешном получении ответа от сервера,
 * функция проверяет, не содержит ли ответ элемент с идентификатором, совпадающим с идентификатором целевого элемента,
 * выбранного по selector. Если совпадение найдено, функция вставляет в целевой элемент только дочернее содержимое элемента из ответа,
 * иначе вставляется полное содержимое ответа.
 * В случае ошибки при запросе, в консоль выводится соответствующее сообщение.
 */
export function createElementApi({ elm = null, url, selector, data = null } = {}) {
  const deferred = $.Deferred(); // Создаем объект Deferred
  const element = $(elm);
  const requestData = data ?? getDataAll(element);

  ajax({
    type: 'POST',
    url: url,
    contentType: 'application/json; charset=utf-8',
    data: JSON.stringify(requestData),
    success: function (response) {
      const responseElement = $(response);
      const target = $(selector);

      // Проверяем, совпадает ли ID первого элемента в ответе с ID целевого элемента.
      if (responseElement.first().attr('id') === target.attr('id')) {
        target.html(''); // Очищаем содержимое целевого элемента.
        target.append(responseElement.contents()); // Вставляем содержимое элемента из ответа.
      } else {
        target.html(response); // Вставляем весь ответ, если ID не совпадают.
      }

      deferred.resolve(target); // Разрешаем промис, передавая целевой элемент
    },
    error: function (xhr) {
      insertHtmlWithScripts({ html: xhr.responseText, parent: document.body });
      this.modalElement = $('.modal.fade:not(.show)').last();
      if (!this.modalElement) return;
      this.modalElement = this.modalElement[0];
      this.modal = new bootstrap.Modal(this.modalElement, {
        keyboard: true
      });
      this.modal.show();
    }
  });

  return deferred.promise(); // Возвращаем промис
}
